import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/admin/embajadores")({
  component: AdminEmbajadores,
});

interface RequestRow {
  id: string;
  user_id: string;
  status: "pending" | "approved" | "rejected";
  requested_at: string;
  reviewed_at: string | null;
  profile: { nickname: string; avatar_url: string | null } | null;
}

interface AmbassadorRow {
  id: string;
  nickname: string;
  avatar_url: string | null;
  ambassador_since: string | null;
}

function AdminEmbajadores() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"pending" | "approved">("pending");
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [ambassadors, setAmbassadors] = useState<AmbassadorRow[]>([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<AmbassadorRow[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createQuery, setCreateQuery] = useState("");
  const [createResults, setCreateResults] = useState<(AmbassadorRow & { email?: string | null })[]>([]);

  const load = async () => {
    const { data: reqs } = await supabase
      .from("ambassador_requests")
      .select("id, user_id, status, requested_at, reviewed_at")
      .order("requested_at", { ascending: false });
    const ids = (reqs ?? []).map((r) => r.user_id);
    const { data: profs } = ids.length
      ? await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids)
      : { data: [] as any[] };
    const map = new Map((profs ?? []).map((p: any) => [p.id, p]));
    setRequests(
      (reqs ?? []).map((r: any) => ({ ...r, profile: map.get(r.user_id) ?? null }))
    );

    const { data: ambs } = await supabase
      .from("profiles")
      .select("id, nickname, avatar_url, ambassador_since")
      .not("ambassador_since", "is", null)
      .order("ambassador_since", { ascending: false });
    setAmbassadors((ambs as AmbassadorRow[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const review = async (id: string, status: "approved" | "rejected") => {
    const { error } = await supabase
      .from("ambassador_requests")
      .update({ status, reviewed_by: user?.id, reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(status === "approved" ? "Embajador aprobado" : "Solicitud rechazada");
    load();
  };

  const revoke = async (userId: string) => {
    const { error } = await supabase.rpc("admin_revoke_ambassador", { target_user: userId });
    if (error) return toast.error(error.message);
    toast.success("Rol revocado");
    load();
  };

  const runSearch = async (q: string) => {
    setSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    const { data } = await supabase
      .from("profiles")
      .select("id, nickname, avatar_url, ambassador_since")
      .ilike("nickname", `%${q}%`)
      .limit(8);
    setSearchResults((data as AmbassadorRow[]) ?? []);
  };

  const assign = async (userId: string) => {
    const { error } = await supabase.rpc("admin_assign_ambassador", { target_user: userId });
    if (error) return toast.error(error.message);
    toast.success("Embajador asignado");
    setSearch(""); setSearchResults([]);
    load();
  };

  const pending = requests.filter((r) => r.status === "pending");

  const runCreateSearch = async (q: string) => {
    setCreateQuery(q);
    if (q.length < 2) { setCreateResults([]); return; }
    const { data } = await supabase
      .from("profiles")
      .select("id, nickname, avatar_url, ambassador_since, email")
      .or(`nickname.ilike.%${q}%,email.ilike.%${q}%`)
      .limit(10);
    setCreateResults((data as any) ?? []);
  };

  const createAmbassador = async (userId: string) => {
    const { error } = await supabase.rpc("admin_assign_ambassador", { target_user: userId });
    if (error) return toast.error(error.message);
    toast.success("Embajador creado");
    setCreateOpen(false); setCreateQuery(""); setCreateResults([]);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl mb-1">Embajadores</h1>
          <p className="text-sm text-muted-foreground">Gestiona solicitudes y embajadores activos.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="shrink-0 rounded-full bg-[#F5C518] text-black px-4 py-2 text-sm font-semibold"
        >
          + Crear embajador
        </button>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setCreateOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl md:rounded-3xl border border-border bg-[#1a1a1a] p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl">Crear embajador</h2>
              <button onClick={() => setCreateOpen(false)} className="rounded-full bg-surface-elevated p-1.5"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-white/60 mb-3">Asigna el rol directamente a un usuario buscándolo por email o nickname.</p>
            <input
              autoFocus
              value={createQuery}
              onChange={(e) => runCreateSearch(e.target.value)}
              placeholder="Email o @nickname…"
              className="w-full rounded-xl border border-border bg-[#0f0f0f] px-3 py-2.5 text-sm"
            />
            <div className="mt-3 space-y-1">
              {createResults.length === 0 && createQuery.length >= 2 && (
                <div className="text-center text-xs text-white/50 py-4">Sin resultados.</div>
              )}
              {createResults.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 rounded-xl bg-[#0f0f0f] px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">@{u.nickname}</div>
                    <div className="text-[11px] text-white/50 truncate">{u.email ?? ""}</div>
                  </div>
                  {u.ambassador_since ? (
                    <span className="text-[11px] text-[#F5C518]">Ya es embajador</span>
                  ) : (
                    <button onClick={() => createAmbassador(u.id)} className="rounded-full bg-[#F5C518] text-black px-3 py-1.5 text-xs font-semibold">
                      Asignar
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      <div className="flex gap-2">
        <button
          onClick={() => setTab("pending")}
          className={`rounded-full px-4 py-1.5 text-sm ${tab === "pending" ? "bg-[#F5C518] text-black font-semibold" : "bg-[#1a1a1a] text-white/70 border border-border"}`}
        >
          Pendientes ({pending.length})
        </button>
        <button
          onClick={() => setTab("approved")}
          className={`rounded-full px-4 py-1.5 text-sm ${tab === "approved" ? "bg-[#F5C518] text-black font-semibold" : "bg-[#1a1a1a] text-white/70 border border-border"}`}
        >
          Aprobados ({ambassadors.length})
        </button>
      </div>

      {tab === "pending" ? (
        <div className="rounded-2xl border border-border bg-[#1a1a1a] divide-y divide-border">
          {pending.length === 0 ? (
            <div className="p-6 text-center text-sm text-white/60">No hay solicitudes pendientes.</div>
          ) : pending.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full overflow-hidden bg-surface-elevated flex items-center justify-center">
                  {r.profile?.avatar_url ? (
                    <img src={r.profile.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold">{r.profile?.nickname?.[0]?.toUpperCase() ?? "?"}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">@{r.profile?.nickname ?? "—"}</div>
                  <div className="text-[11px] text-white/50">
                    Solicitado {new Date(r.requested_at).toLocaleDateString("es-ES")}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => review(r.id, "approved")}
                  className="flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 text-xs font-semibold"
                >
                  <Check className="h-3.5 w-3.5" /> Aprobar
                </button>
                <button
                  onClick={() => review(r.id, "rejected")}
                  className="flex items-center gap-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 px-3 py-1.5 text-xs font-semibold"
                >
                  <X className="h-3.5 w-3.5" /> Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-[#1a1a1a] p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-white/50">Asignar manualmente</div>
            <input
              value={search}
              onChange={(e) => runSearch(e.target.value)}
              placeholder="Buscar por nickname…"
              className="w-full rounded-xl border border-border bg-[#0f0f0f] px-3 py-2 text-sm"
            />
            {searchResults.length > 0 && (
              <div className="space-y-1">
                {searchResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => assign(u.id)}
                    className="flex w-full items-center justify-between rounded-xl bg-[#0f0f0f] px-3 py-2 text-left text-sm hover:bg-[#222]"
                  >
                    <span>@{u.nickname}</span>
                    <span className="text-xs text-[#F5C518]">Asignar</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-[#1a1a1a] divide-y divide-border">
            {ambassadors.length === 0 ? (
              <div className="p-6 text-center text-sm text-white/60">Aún no hay embajadores.</div>
            ) : ambassadors.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-surface-elevated flex items-center justify-center">
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Star className="h-4 w-4 text-[#F5C518]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">@{a.nickname}</div>
                    <div className="text-[11px] text-white/50">
                      Desde {a.ambassador_since ? new Date(a.ambassador_since).toLocaleDateString("es-ES") : "—"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => revoke(a.id)}
                  className="rounded-full bg-red-500/15 text-red-400 border border-red-500/30 px-3 py-1.5 text-xs font-semibold"
                >
                  Revocar
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
